import { useState, useCallback, useEffect } from 'react';
import type { AgentWithId } from '@molt-starter/shared';
import { getAgentProfile, getErrorMessage } from '@/lib/api';

interface UseAgentProfileReturn {
  agent: AgentWithId | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching an agent profile by ID
 */
export const useAgentProfile = (agentId: string | undefined): UseAgentProfileReturn => {
  const [agent, setAgent] = useState<AgentWithId | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAgent = useCallback(async () => {
    if (!agentId) {
      setAgent(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getAgentProfile(agentId);
      setAgent(result);
    } catch (err) {
      console.error('Failed to fetch agent:', err);
      setError(getErrorMessage(err));
      setAgent(null);
    } finally {
      setIsLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  return {
    agent,
    isLoading,
    error,
    refetch: fetchAgent,
  };
};

export default useAgentProfile;
